<script setup lang="ts">
import { useEditor, EditorContent } from '@tiptap/vue-3'
import Placeholder from '@tiptap/extension-placeholder'
import { ESection } from '@/components/base/Email'
import EmailBlockWrapper from './EmailBlockWrapper.vue'
import EmailTiptapToolbarWrapper from '../tiptap/EmailTiptapToolbarWrapper.vue'
import EmailPanelSection from '../EmailPanelSection.vue'
import ColorPickerWithPresets from '../ColorPickerWithPresets.vue'
import { HeadingCleanup } from '../tiptap/HeadingCleanupExtension'
import { createEmailEditorExtensions } from '../../utils/tiptapExtensions'
import { emailVariables, getVariableOptions, type EmailVariable } from '../../constants/variables'
import { createVariableSuggestion, type VariableSuggestionState } from '../tiptap/variableSuggestion'
import EmailVariableCommandList from '../EmailVariableCommandList.vue'
import type { TextBlock, TiptapContent } from '../../types'

const { t } = useI18n()

const suggestion = reactive({
  open: false,
  items: [] as EmailVariable[],
  activeIndex: 0,
  top: 0,
  left: 0,
  apply: null as ((variable: EmailVariable) => void) | null
})

const props = defineProps<{
  block: TextBlock
  isSelected: boolean
  colorPresets?: { color: string; label: string }[]
}>()

const emit = defineEmits<{
  (e: 'select'): void
  (e: 'focus'): void
  (e: 'blur'): void
  (e: 'update:content', content: TiptapContent): void
  (e: 'update:block', block: TextBlock): void
}>()

const isInternalUpdate = ref(false)

const variables = computed(() => getVariableOptions(emailVariables))

const placeholderText = t('pages.email.emailTemplateCreatePage.panel.defaultText')

const variableSuggestion = createVariableSuggestion({
  isOpen: () => suggestion.open,
  onStart: (state) => openSuggestion(state),
  onUpdate: (state) => openSuggestion(state),
  onExit: () => {
    suggestion.open = false
  },
  onArrowDown: () => moveSuggestion(1),
  onArrowUp: () => moveSuggestion(-1),
  onEnter: () => {
    const variable = suggestion.items[suggestion.activeIndex]
    if (variable) applySuggestion(variable)
    return true
  }
})

const editor = useEditor({
  content: props.block.content,
  extensions: [
    ...createEmailEditorExtensions({ disableGapcursor: true }),
    HeadingCleanup,
    variableSuggestion,
    Placeholder.configure({
      placeholder: placeholderText
    })
  ],
  editorProps: {
    attributes: {
      class: 'focus:outline-none'
    }
  },
  onUpdate: ({ editor }) => {
    isInternalUpdate.value = true
    emit('update:content', editor.getJSON())
    nextTick(() => {
      isInternalUpdate.value = false
    })
  },
  onFocus: () => {
    emit('select')
    emit('focus')
  },
  onBlur: () => {
    suggestion.open = false
    emit('blur')
  }
})

watch(
  () => props.block.content,
  (newValue) => {
    if (isInternalUpdate.value) return
    if (editor.value) {
      editor.value.commands.setContent(newValue, { emitUpdate: false })
    }
  },
  { deep: true }
)

const insertVariable = (code: string) => {
  editor.value?.commands.insertVariable(code)
}

function openSuggestion(state: VariableSuggestionState) {
  suggestion.items = state.items
  suggestion.apply = state.command
  if (state.rect) {
    suggestion.top = state.rect.bottom + 4
    suggestion.left = state.rect.left
  }
  if (suggestion.activeIndex >= state.items.length) suggestion.activeIndex = 0
  suggestion.open = state.items.length > 0
}

function moveSuggestion(delta: number): boolean {
  const count = suggestion.items.length
  if (count === 0) return false
  suggestion.activeIndex = (suggestion.activeIndex + delta + count) % count
  return true
}

function applySuggestion(variable: EmailVariable) {
  suggestion.apply?.(variable)
  suggestion.open = false
}

onBeforeUnmount(() => {
  editor.value?.destroy()
})
</script>

<template>
  <EmailBlockWrapper
    :is-selected="isSelected"
    :label="t('pages.email.emailTemplateCreatePage.blockText')"
    cursor="text"
    :block-background-color="block.blockBackgroundColor"
    @select="emit('select')"
  >
    <ESection :style="{ padding: '10px 0' }">
      <EditorContent
        :editor="editor"
        class="text-block-editor"
        :style="{
          fontSize: '16px',
          lineHeight: '1.6',
          textAlign: block.align,
          color: `#${block.color}`
        }"
      />
    </ESection>
  </EmailBlockWrapper>

  <EmailTiptapToolbarWrapper
    :editor="editor"
    :visible="isSelected"
    :variables="variables"
    :color-presets="colorPresets"
    @insert-variable="insertVariable"
  />

  <Teleport to="#email-variable-popups" defer>
    <div
      v-if="suggestion.open"
      class="fixed z-[100]"
      :style="{ top: `${suggestion.top}px`, left: `${suggestion.left}px` }"
    >
      <EmailVariableCommandList
        :items="suggestion.items"
        :active-index="suggestion.activeIndex"
        @select="applySuggestion"
        @hover="suggestion.activeIndex = $event"
      />
    </div>
  </Teleport>

  <Teleport v-if="isSelected" to="#email-settings-panel" defer>
    <div class="space-y-8 px-4 pb-6 pt-4">
      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.blockBackground')">
        <ColorPickerWithPresets
          :model-value="block.blockBackgroundColor ?? 'ffffff'"
          :presets="colorPresets ?? []"
          @update:model-value="(v) => emit('update:block', { ...block, blockBackgroundColor: v })"
        />
      </EmailPanelSection>
    </div>
  </Teleport>
</template>

<style scoped>
@reference "@/input.css";

.text-block-editor :deep(.ProseMirror) {
  @apply outline-none min-h-6;
}
.text-block-editor :deep(p) {
  @apply m-0;
}
.text-block-editor :deep(code) {
  @apply bg-muted rounded px-1 py-0.5 text-sm text-rose-600;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}
.text-block-editor :deep(h1) {
  font-size: 32px;
  @apply font-bold leading-tight m-0;
}
.text-block-editor :deep(h2) {
  font-size: 24px;
  @apply font-semibold leading-snug m-0;
}
.text-block-editor :deep(h3) {
  font-size: 20px;
  @apply font-semibold leading-normal m-0;
}
.text-block-editor :deep(h4) {
  font-size: 18px;
  @apply font-semibold leading-normal m-0;
}
.text-block-editor :deep(h5) {
  font-size: 16px;
  @apply font-semibold leading-normal m-0;
}
.text-block-editor :deep(h6) {
  font-size: 14px;
  @apply font-semibold leading-normal m-0;
}
.text-block-editor :deep(ul) {
  @apply pl-6 my-2;
  list-style-type: disc;
}
.text-block-editor :deep(ol) {
  @apply pl-6 my-2;
  list-style-type: decimal;
}
.text-block-editor :deep(li) {
  display: list-item;
  @apply mb-1;
}
.text-block-editor :deep(li p) {
  @apply m-0;
}
.text-block-editor :deep(mark) {
  @apply rounded-sm px-0.5;
}
.text-block-editor :deep(blockquote) {
  @apply border-l-2 border-border pl-3 my-2 text-foreground-secondary italic;
}
.text-block-editor :deep(s) {
  @apply line-through;
}
.text-block-editor :deep(a) {
  @apply text-blue-600 underline;
}
.text-block-editor :deep(.variable-node),
.text-block-editor :deep(.variable-tag) {
  @apply inline-flex items-center px-2 py-0.5 mx-0.5 bg-muted border border-border rounded text-sm font-medium text-foreground-secondary;
}
.text-block-editor :deep(p.is-editor-empty:first-child::before) {
  @apply text-muted-foreground italic float-left h-0 pointer-events-none;
  content: attr(data-placeholder);
}
</style>
